import { useState, useEffect, useMemo, useRef } from 'react'
import {
  calculateAccountTradeFee,
  formatSuggestedFee,
  resolveFeeRuleForTrade,
  type AccountFeeConfig,
} from '../lib/accountFeeRules'
import { fetchFeeConfigsForAccounts } from '../lib/accountFeeRulesDb'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNotification } from '../contexts/NotificationContext'
import { supabase } from '../lib/supabase'
import SymbolSelector from './SymbolSelector'
import LocalizedDateTimeInput from './LocalizedDateTimeInput'

export interface AddTradeTrade {
  id: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  symbol: string
  trade_type: 'spot' | 'future'
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  entry_date: string | null
  entry_price: number
  stop_loss: number | null
  take_profit: number | null
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  notes: string | null
  strategy: string | null
  strategy_id: string | null
  currency: string | null
  position_size: number | null
  pnl: number | null
  pnl_percentage: number | null
  total_entry_amount?: number | null
  total_exit_amount?: number | null
  entry_setup_image: string | null
  exit_setup_image: string | null
  time_interval: string | null
  is_public?: boolean | null
  public_slug?: string | null
  public_visibility?: 'link' | 'community' | 'restricted' | null
  account_id?: string | null
  exchange?: string | null
  tags?: string[] | null
  fee?: number | null
  journal_confidence?: number | null
  created_via?: string | null
}

interface AddTradeProps {
  onClose: () => void
  onSuccess?: () => void
  /** When true, render as embedded page content (no modal overlay). */
  embedded?: boolean
  /** When provided, form is in edit mode: prefill from trade and submit updates instead of insert. */
  trade?: AddTradeTrade | null
}

/** Stable section wrapper - must be outside AddTrade to avoid remounting form children (e.g. SymbolSelector). */
function AddTradeSection({
  title,
  children,
  highlight = 'normal',
}: {
  title: string
  children: React.ReactNode
  highlight?: 'current' | 'done' | 'upcoming' | 'normal'
}) {
  const isCurrent = highlight === 'current'
  const isUpcoming = highlight === 'upcoming'
  return (
    <div
      className={`h-full flex flex-col gap-3 transition-all duration-300 rounded-xl ${
        isCurrent ? 'border-2 border-[#A5D6A7] bg-[#E8F5E9] p-3 md:p-4' : ''
      } ${isUpcoming ? 'opacity-60' : ''}`}
    >
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">{title}</h3>
      <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-5 shadow-sm space-y-4 flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  )
}

export default function AddTrade({ onClose, onSuccess, embedded, trade: editTrade }: AddTradeProps) {
  const { t } = useTranslation('trading')
  const { user } = useAuth()
  const { canPublishTrades, canUseAccounts, profileLoading, membershipLimits } = useMembership()
  const { showSuccess, showConfirm, showWarning } = useNotification()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isEdit = Boolean(editTrade)
  const initialWasPublic = Boolean(editTrade?.is_public)

  // Form state (prefill from editTrade when in edit mode)
  const [market, setMarket] = useState<'Commodity' | 'Crypto' | 'Forex' | 'Stock'>(() => editTrade?.market ?? 'Stock')
  const [symbol, setSymbol] = useState(() => editTrade?.symbol ?? '')
  const [symbolExchange, setSymbolExchange] = useState<string | null>(() => editTrade?.exchange ?? null)
  const [tradeType, setTradeType] = useState<'spot' | 'future'>(() => editTrade?.trade_type ?? 'spot')
  const [longShort, setLongShort] = useState<'long' | 'short'>(() => editTrade?.long_short ?? 'long')
  const [quantity, setQuantity] = useState(() => editTrade?.quantity?.toString() ?? '')
  const [contractSize, setContractSize] = useState(() => editTrade?.contract_size?.toString() ?? '')
  const [entryDate, setEntryDate] = useState(() =>
    editTrade?.entry_date ? new Date(editTrade.entry_date).toISOString().slice(0, 16) : ''
  )
  const [entryPrice, setEntryPrice] = useState(() => editTrade?.entry_price?.toString() ?? '')
  const [stopLoss, setStopLoss] = useState(() => editTrade?.stop_loss?.toString() ?? '')
  const [takeProfit, setTakeProfit] = useState(() => editTrade?.take_profit?.toString() ?? '')
  const [exitDate, setExitDate] = useState(() =>
    editTrade?.exit_date ? new Date(editTrade.exit_date).toISOString().slice(0, 16) : ''
  )
  const [exitPrice, setExitPrice] = useState(() => editTrade?.exit_price?.toString() ?? '')
  const [tradeSetup, setTradeSetup] = useState(() => editTrade?.trade_setup ?? '')
  const [notes, setNotes] = useState(() => editTrade?.notes ?? '')
  const [strategyId, setStrategyId] = useState<string>(() => editTrade?.strategy_id ?? '')
  const [accountId, setAccountId] = useState<string>(() => editTrade?.account_id ?? '')
  const [tagsArray, setTagsArray] = useState<string[]>(() =>
    Array.isArray(editTrade?.tags) ? [...editTrade.tags] : []
  )
  const [tagInputValue, setTagInputValue] = useState('')
  const [strategies, setStrategies] = useState<Array<{ id: string; display_name: string }>>([])
  const [accounts, setAccounts] = useState<Array<{ id: string; platform: string; currency: string }>>([])
  const [strategiesLoading, setStrategiesLoading] = useState(true)
  const [accountsLoading, setAccountsLoading] = useState(true)
  const [currency, setCurrency] = useState(() => editTrade?.currency ?? 'USD')
  const [entrySetupImage, setEntrySetupImage] = useState(() => editTrade?.entry_setup_image ?? '')
  const [exitSetupImage, setExitSetupImage] = useState(() => editTrade?.exit_setup_image ?? '')
  const [timeInterval, setTimeInterval] = useState<string>(() => editTrade?.time_interval ?? '')
  const [entryFeeInput, setEntryFeeInput] = useState(() =>
    editTrade?.fee != null ? String(editTrade.fee) : ''
  )
  const [exitFeeInput, setExitFeeInput] = useState('')
  const [accountFeeConfigs, setAccountFeeConfigs] = useState<Map<string, AccountFeeConfig>>(new Map())
  const entryFeeTouchedRef = useRef(editTrade?.fee != null)
  const [isPublic, setIsPublic] = useState(() => Boolean(editTrade?.is_public))
  const [publicVisibility, setPublicVisibility] = useState<'link' | 'community' | 'restricted'>(() =>
    (editTrade?.public_visibility as 'link' | 'community' | 'restricted') || 'link'
  )
  
  // Calculated RR
  const [rr, setRr] = useState<number | null>(() => editTrade?.rr ?? null)
  
  // Calculated Position Size and P&L
  const [positionSize, setPositionSize] = useState<number | null>(() => editTrade?.position_size ?? null)
  const [pnl, setPnl] = useState<number | null>(() => editTrade?.pnl ?? null)
  const [pnlPercentage, setPnlPercentage] = useState<number | null>(() => editTrade?.pnl_percentage ?? null)

  /** Which sections are prominent (green card): [0]=basics, [1]=entry, [2]=risk, [3]=exit. */
  const prominentSections = useMemo(() => {
    const hasBasics = !!market && !!symbol.trim()
    const hasEntry = !!entryDate && !!entryPrice && (!!quantity || parseFloat(quantity) > 0)
    const hasRisk = !!stopLoss && !!takeProfit && parseFloat(stopLoss) > 0 && parseFloat(takeProfit) > 0
    if (hasEntry && hasRisk) return [3]
    if (hasEntry) return [2]
    if (hasBasics) return [0, 1]
    return [0]
  }, [market, symbol, entryDate, entryPrice, quantity, stopLoss, takeProfit])

  // Calculate Position Size: quantity * entry_price
  useEffect(() => {
    const qty = parseFloat(quantity)
    const entry = parseFloat(entryPrice)
    
    if (qty && entry && qty > 0 && entry > 0) {
      setPositionSize(qty * entry)
    } else {
      setPositionSize(null)
    }
  }, [quantity, entryPrice])

  // Parse fee input: "10" or "10.5" = amount, "10%" or "10.5%" = percentage
  const parseFeeInput = (input: string): { isPercent: boolean; value: number } | null => {
    const s = input.trim()
    if (!s) return null
    const isPercent = s.endsWith('%')
    const numStr = isPercent ? s.slice(0, -1) : s
    const value = parseFloat(numStr)
    if (isNaN(value) || value < 0) return null
    return { isPercent, value }
  }

  // Calculate total fee from entry/exit fee inputs (amount or % of entry/exit)
  const totalFee = useMemo(() => {
    const qty = parseFloat(quantity)
    const entry = parseFloat(entryPrice)
    const exit = parseFloat(exitPrice)
    const entryAmount = qty > 0 && entry > 0 ? qty * entry : 0
    const exitAmount = qty > 0 && exit > 0 ? qty * exit : 0

    let entryFeeAmt = 0
    const entryParsed = parseFeeInput(entryFeeInput)
    if (entryParsed) {
      entryFeeAmt = entryParsed.isPercent ? (entryAmount * entryParsed.value) / 100 : entryParsed.value
    }

    let exitFeeAmt = 0
    const exitParsed = parseFeeInput(exitFeeInput)
    if (exitParsed) {
      exitFeeAmt = exitParsed.isPercent ? (exitAmount * exitParsed.value) / 100 : exitParsed.value
    }

    return entryFeeAmt + exitFeeAmt
  }, [entryFeeInput, exitFeeInput, quantity, entryPrice, exitPrice])

  // Calculate P&L: gross P&L minus fees
  useEffect(() => {
    const qty = parseFloat(quantity)
    const entry = parseFloat(entryPrice)
    const exit = parseFloat(exitPrice)
    
    if (qty && entry && exit && qty > 0 && entry > 0 && exit > 0) {
      let grossPnl: number
      if (longShort === 'long') {
        grossPnl = (exit - entry) * qty
      } else {
        grossPnl = (entry - exit) * qty
      }
      const netPnl = grossPnl - totalFee
      setPnl(netPnl)
      
      const posSize = qty * entry
      if (posSize > 0) {
        setPnlPercentage((netPnl / posSize) * 100)
      } else {
        setPnlPercentage(null)
      }
    } else {
      setPnl(null)
      setPnlPercentage(null)
    }
  }, [exitPrice, entryPrice, quantity, longShort, totalFee])

  // Calculate RR automatically when entry_price, stop_loss, or take_profit changes
  useEffect(() => {
    const entry = parseFloat(entryPrice)
    const stop = parseFloat(stopLoss)
    const tp = parseFloat(takeProfit)

    if (!entry || !stop || !tp || entry <= 0 || stop <= 0 || tp <= 0) {
      setRr(null)
      return
    }

    let calculatedRr: number | null = null

    if (longShort === 'long') {
      // Long position: entry > stop, tp > entry
      if (entry <= stop || tp <= entry) {
        setRr(null)
        return
      }
      const risk = entry - stop
      const reward = tp - entry
      if (risk > 0) {
        calculatedRr = reward / risk
      }
    } else {
      // Short position: entry < stop, tp < entry
      if (entry >= stop || tp >= entry) {
        setRr(null)
        return
      }
      const risk = stop - entry
      const reward = entry - tp
      if (risk > 0) {
        calculatedRr = reward / risk
      }
    }

    setRr(calculatedRr)
  }, [entryPrice, stopLoss, takeProfit, longShort])

  // Fetch strategies
  useEffect(() => {
    const fetchStrategies = async () => {
      if (!user) return

      try {
        setStrategiesLoading(true)
        // Fetch user's strategies and public strategies
        const { data, error } = await supabase
          .from('strategies')
          .select('id, display_name')
          .or(`user_id.eq.${user.id},is_public.eq.true`)
          .eq('is_active', true)
          .order('display_name', { ascending: true })

        if (error) throw error

        setStrategies(data || [])
      } catch (err: any) {
        console.error('Failed to load strategies:', err)
      } finally {
        setStrategiesLoading(false)
      }
    }

    fetchStrategies()
  }, [user])

  // Fetch user's accounts (for account selection when canUseAccounts)
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user || !canUseAccounts) {
        setAccountsLoading(false)
        return
      }

      try {
        setAccountsLoading(true)
        const { data, error } = await supabase
          .from('accounts')
          .select('id, platform, currency')
          .eq('user_id', user.id)
          .order('platform', { ascending: true })

        if (error) throw error

        const rows = data || []
        setAccounts(rows)
        if (rows.length) {
          try {
            const configs = await fetchFeeConfigsForAccounts(supabase, rows.map((row) => row.id))
            setAccountFeeConfigs(configs)
          } catch (configError) {
            console.error('Failed to load account fee rules:', configError)
            setAccountFeeConfigs(new Map())
          }
        } else {
          setAccountFeeConfigs(new Map())
        }
      } catch (err: any) {
        console.error('Failed to load accounts:', err)
        setAccounts([])
        setAccountFeeConfigs(new Map())
      } finally {
        setAccountsLoading(false)
      }
    }

    fetchAccounts()
  }, [user, canUseAccounts])

  useEffect(() => {
    entryFeeTouchedRef.current = false
  }, [accountId])

  useEffect(() => {
    setSymbolExchange(null)
  }, [market])

  useEffect(() => {
    if (entryFeeTouchedRef.current) return
    if (editTrade?.fee != null) return
    if (!accountId) return
    const config = accountFeeConfigs.get(accountId)
    const rule = resolveFeeRuleForTrade(config, market, symbolExchange)
    const qty = parseFloat(quantity)
    const entry = parseFloat(entryPrice)
    const notional = qty > 0 && entry > 0 ? qty * entry : 0
    const suggested = calculateAccountTradeFee(rule, notional)
    if (suggested != null) setEntryFeeInput(formatSuggestedFee(suggested))
  }, [accountId, market, symbolExchange, quantity, entryPrice, accountFeeConfigs, editTrade?.fee])

  // Set default entry date to now on mount (add mode only; edit mode uses trade's date)
  useEffect(() => {
    if (!editTrade && !entryDate) {
      const now = new Date()
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
      setEntryDate(localDateTime)
    }
  }, [editTrade, entryDate])

  const resetForm = () => {
    localStorage.removeItem('addTrade_draft')
    setError(null)
    setMarket('Stock')
    setSymbol('')
    setTradeType('spot')
    setLongShort('long')
    setQuantity('')
    setContractSize('')
    setEntryDate('')
    setEntryPrice('')
    setStopLoss('')
    setTakeProfit('')
    setExitDate('')
    setExitPrice('')
    setTradeSetup('')
    setNotes('')
    setStrategyId('')
    setAccountId('')
    setTagsArray([])
    setTagInputValue('')
    setCurrency('USD')
    setEntrySetupImage('')
    setExitSetupImage('')
    setEntryFeeInput('')
    setExitFeeInput('')
    setTimeInterval('')
    setIsPublic(false)
    setPublicVisibility('link')
    setRr(null)
    setPositionSize(null)
    setPnl(null)
    setPnlPercentage(null)
  }

  // Set contract size based on trade type
  useEffect(() => {
    if (tradeType === 'spot') {
      setContractSize('1')
    } else if (tradeType === 'future') {
      setContractSize('100')
    }
  }, [tradeType])


  const copyShareLink = async (slug: string) => {
    const shareUrl = `${window.location.origin}/p/${slug}`
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl)
        showSuccess('Public link copied to clipboard')
      } else {
        window.prompt('Copy this link:', shareUrl)
      }
    } catch {
      window.prompt('Copy this link:', shareUrl)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!user) {
      setError('You must be logged in to save trades')
      return
    }

    // Validation
    if (!symbol.trim()) {
      setError('Symbol is required')
      return
    }
    if (!entryPrice) {
      setError('Entry price is required')
      return
    }

    // Limits only for add mode; edit mode has different rules below
    if (!isEdit) {
      // Check max_trades_year limit (trades created in current calendar year)
      if (membershipLimits?.max_trades_year !== null) {
        try {
          const now = new Date()
          const yearStart = new Date(now.getFullYear(), 0, 1) // January 1st of current year
          const yearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59) // December 31st of current year

          const { count, error: countError } = await supabase
            .from('trades')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .gte('created_at', yearStart.toISOString())
            .lte('created_at', yearEnd.toISOString())

          if (countError) throw countError

          const currentYearTrades = count || 0
          const maxTradesYear = membershipLimits.max_trades_year

          if (currentYearTrades >= maxTradesYear) {
            setError(`You have reached the maximum number of trades for this year (${maxTradesYear}). Please upgrade your subscription to create more trades.`)
            return
          }
        } catch (limitCheckError: any) {
          console.error('Failed to check yearly trades limit:', limitCheckError)
        }
      }

      // Check max_open_trades limit if this will be an open trade (no exit_date)
      if (!exitDate && membershipLimits?.max_open_trades !== null) {
        try {
          const { count, error: countError } = await supabase
            .from('trades')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .is('exit_date', null)

          if (countError) throw countError

          const currentOpenTrades = count || 0
          const maxOpenTrades = membershipLimits.max_open_trades

          if (currentOpenTrades >= maxOpenTrades) {
            setError(`You have reached the maximum number of open trades (${maxOpenTrades}). Please upgrade your subscription or close some trades before opening new ones.`)
            return
          }
        } catch (limitCheckError: any) {
          console.error('Failed to check open trades limit:', limitCheckError)
        }
      }
    } else {
      // Edit: check max_open_trades only when reopening (was closed, now open)
      const wasClosed = editTrade!.exit_date !== null
      const willBeOpen = !exitDate
      if (wasClosed && willBeOpen && membershipLimits?.max_open_trades !== null) {
        try {
          const { count, error: countError } = await supabase
            .from('trades')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .is('exit_date', null)
            .neq('id', editTrade!.id)

          if (countError) throw countError

          const currentOpenTrades = count || 0
          const maxOpenTrades = membershipLimits.max_open_trades

          if (currentOpenTrades >= maxOpenTrades) {
            setError(`You have reached the maximum number of open trades (${maxOpenTrades}). Please upgrade your subscription or close some trades before opening this one.`)
            return
          }
        } catch (limitCheckError: any) {
          console.error('Failed to check open trades limit:', limitCheckError)
        }
      }
    }

    const publishAllowedForTrade = canPublishTrades || (isEdit && initialWasPublic)
    const willBePublic = publishAllowedForTrade ? isPublic : false

    // Warn user: making a trade public is irreversible (add) or turning it public (edit)
    if (!initialWasPublic && willBePublic) {
      const confirmed = await showConfirm({
        title: t('tradeList.publicTradeIrreversibleTitle'),
        message: t('tradeList.publicTradeIrreversibleWarning'),
        confirmText: t('tradeList.publicTradeIrreversibleConfirm'),
        cancelText: t('buttons.cancel', { ns: 'common' }),
        confirmButtonStyle: 'danger'
      })
      if (!confirmed) return
    }

    setLoading(true)

    try {
      const baseData: any = {
        market,
        symbol: symbol.trim(),
        trade_type: tradeType,
        long_short: longShort,
        is_public: willBePublic,
        public_visibility: willBePublic ? publicVisibility : 'link',
        entry_date: entryDate ? new Date(entryDate).toISOString() : null,
        entry_price: parseFloat(entryPrice),
        stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfit ? parseFloat(takeProfit) : null,
        exit_date: exitDate ? new Date(exitDate).toISOString() : null,
        trade_setup: tradeSetup.trim() || null,
        notes: notes.trim() || null,
        strategy_id: strategyId || null,
        currency: currency.trim() || null,
        entry_setup_image: entrySetupImage.trim() || null,
        exit_setup_image: exitSetupImage.trim() || null,
        time_interval: timeInterval.trim() || null,
      }

      // Tags: include any current input word (max 5)
      const finalTags = [...tagsArray]
      if (tagInputValue.trim() && finalTags.length < 5) {
        finalTags.push(tagInputValue.trim())
      }
      baseData.tags = finalTags.length > 0 ? finalTags.slice(0, 5) : null

      // Account: only when canUseAccounts
      if (canUseAccounts) {
        baseData.account_id = accountId || null
      }

      if (!isEdit) {
        baseData.user_id = user.id
      }

      // Closed trades (exit_date set) use quantity 0 / position_size 0 (DB handles totals)
      if (exitDate) {
        baseData.quantity = 0
        baseData.position_size = 0
      } else {
        if (quantity) baseData.quantity = parseFloat(quantity)
        if (quantity && entryPrice) {
          const qty = parseFloat(quantity)
          const entry = parseFloat(entryPrice)
          if (qty > 0 && entry > 0) baseData.position_size = qty * entry
        }
      }
      if (contractSize) baseData.contract_size = parseFloat(contractSize)
      if (exitPrice) baseData.exit_price = parseFloat(exitPrice)
      if (rr !== null) baseData.rr = rr

      // Total fee from entry + exit (amount or % of entry/exit)
      const qty = parseFloat(quantity)
      const entry = parseFloat(entryPrice)
      const exit = parseFloat(exitPrice)
      const entryAmt = qty > 0 && entry > 0 ? qty * entry : 0
      const exitAmt = qty > 0 && exit > 0 ? qty * exit : 0
      let submitTotalFee = 0
      const entryFeeParsed = parseFeeInput(entryFeeInput)
      if (entryFeeParsed) {
        submitTotalFee += entryFeeParsed.isPercent ? (entryAmt * entryFeeParsed.value) / 100 : entryFeeParsed.value
      }
      const exitFeeParsed = parseFeeInput(exitFeeInput)
      if (exitFeeParsed) {
        submitTotalFee += exitFeeParsed.isPercent ? (exitAmt * exitFeeParsed.value) / 100 : exitFeeParsed.value
      }
      baseData.fee = submitTotalFee > 0 ? submitTotalFee : null

      // P&L when exit price set (gross minus fees)
      if (exitPrice && quantity && entryPrice && qty > 0 && entry > 0 && exit > 0) {
        let grossPnl: number
        if (longShort === 'long') {
          grossPnl = (exit - entry) * qty
        } else {
          grossPnl = (entry - exit) * qty
        }
        const netPnl = grossPnl - submitTotalFee
        baseData.pnl = netPnl
        const posSize = qty * entry
        if (posSize > 0) {
          baseData.pnl_percentage = (netPnl / posSize) * 100
        }
      }

      if (isEdit && editTrade) {
        const { data: updated, error: updateError } = await supabase
          .from('trades')
          .update(baseData)
          .eq('id', editTrade.id)
          .select('public_slug')
          .single()

        if (updateError) throw updateError

        if (willBePublic && publicVisibility === 'link' && updated?.public_slug) {
          await copyShareLink(updated.public_slug)
        }

        if (onSuccess) onSuccess()
        onClose()
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from('trades')
          .insert([baseData])
          .select('id, public_slug')
          .single()

        if (insertError) throw insertError

        if (willBePublic && publicVisibility === 'link' && inserted?.public_slug) {
          await copyShareLink(inserted.public_slug)
        }

        localStorage.removeItem('addTrade_draft')
        if (onSuccess) onSuccess()
        onClose()
      }
    } catch (err: any) {
      setError(err.message || (isEdit ? 'Failed to update trade' : 'Failed to save trade'))
      setLoading(false)
    }
  }

  const formCard = (
    <div
      className={`rounded-2xl w-full overflow-y-auto ${embedded ? 'overflow-x-hidden shadow-none min-w-0' : 'p-5 max-w-2xl shadow-xl'}`}
      style={{ backgroundColor: embedded ? 'transparent' : '#FBFBFB' }}
    >
      {!embedded && (
        <div className="flex justify-end items-center mb-5 sticky top-0 pb-3 border-b" style={{ backgroundColor: '#FBFBFB' }}>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
        <form onSubmit={handleSubmit} className={embedded ? 'space-y-8' : 'space-y-5'}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Row 1: 3 columns — Trade basics | Entry | Risk & target (all vertical, equal height) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0 items-stretch">
            {/* Column 1: Trade basics (vertical) */}
            <AddTradeSection
              title={t('addTrade.sectionBasic')}
              highlight={prominentSections.includes(0) ? 'current' : prominentSections.some((i) => i > 0) ? 'done' : 'upcoming'}
            >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('addTrade.market')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={market}
                  onChange={(e) => setMarket(e.target.value as any)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value="Commodity">Commodity</option>
                  <option value="Crypto">Crypto</option>
                  <option value="Forex">Forex</option>
                  <option value="Stock">Stock</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('addTrade.symbol')} <span className="text-red-500">*</span>
                </label>
                <SymbolSelector
                  market={market}
                  value={symbol}
                  onChange={(newSymbol, symbolData) => {
                    setSymbol(newSymbol)
                    setSymbolExchange(symbolData?.exchange ?? null)
                  }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('addTrade.type')} <span className="text-red-500">*</span>
                </label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setTradeType('spot')}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      tradeType === 'spot' ? 'bg-orange-500 text-white' : 'bg-app-box text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('addTrade.spot')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTradeType('future')}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                      tradeType === 'future' ? 'bg-orange-500 text-white' : 'bg-app-box text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('addTrade.future')}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('addTrade.position')} <span className="text-red-500">*</span>
                </label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setLongShort('long')}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      longShort === 'long' ? 'bg-green-500 text-white' : 'bg-app-box text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('addTrade.long')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLongShort('short')}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                      longShort === 'short' ? 'bg-red-500 text-white' : 'bg-app-box text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('addTrade.short')}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="TRY">TRY</option>
                  <option value="SEK">SEK</option>
                  <option value="DKK">DKK</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            </AddTradeSection>

            {/* Column 2: Entry (vertical) */}
            <AddTradeSection
              title={t('addTrade.sectionEntry')}
              highlight={prominentSections.includes(1) ? 'current' : prominentSections.some((i) => i > 1) ? 'done' : 'upcoming'}
            >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('addTrade.entryDate')} <span className="text-red-500">*</span>
                </label>
                <LocalizedDateTimeInput
                  value={entryDate}
                  onChange={setEntryDate}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('addTrade.entryPrice')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="any"
                  value={entryPrice}
                  onChange={(e) => setEntryPrice(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder={t('addTrade.entryPricePlaceholder')}
                />
              </div>
              {tradeType === 'future' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.quantity')}</label>
                    <input
                      type="number"
                      step="any"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder={t('addTrade.quantityPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contract Size</label>
                    <input
                      type="number"
                      step="any"
                      value={contractSize}
                      onChange={(e) => setContractSize(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="Contract Size"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.quantity')}</label>
                  <input
                    type="number"
                    step="any"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder={t('addTrade.quantityPlaceholder')}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entry Setup Image URL</label>
                <input
                  type="url"
                  value={entrySetupImage}
                  onChange={(e) => setEntrySetupImage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder="https://example.com/image.png"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.entryFee')}</label>
                <input
                  type="text"
                  value={entryFeeInput}
                  onChange={(e) => {
                    entryFeeTouchedRef.current = true
                    setEntryFeeInput(e.target.value)
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder={t('addTrade.feePlaceholder')}
                />
              </div>
            </div>
            </AddTradeSection>

            {/* Column 3: Risk & target (vertical) + Time Interval */}
            <AddTradeSection
              title={t('addTrade.sectionRisk')}
              highlight={prominentSections.includes(2) ? 'current' : prominentSections.some((i) => i > 2) ? 'done' : 'upcoming'}
            >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.stopLoss')}</label>
                <input
                  type="number"
                  step="any"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder={t('addTrade.stopLossPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.takeProfit')}</label>
                <input
                  type="number"
                  step="any"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder={t('addTrade.takeProfitPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Risk/Reward (RR)</label>
                <input
                  type="text"
                  value={rr !== null ? rr.toFixed(2) : '-'}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.strategy')}</label>
                <select
                  value={strategyId}
                  onChange={(e) => setStrategyId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value="">{t('addTrade.strategyPlaceholder')}</option>
                  {strategiesLoading ? (
                    <option disabled>Loading strategies...</option>
                  ) : (
                    strategies.map((strategy) => (
                      <option key={strategy.id} value={strategy.id}>
                        {strategy.display_name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Interval</label>
                <select
                  value={timeInterval}
                  onChange={(e) => setTimeInterval(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value="">Select Interval</option>
                  <option value="1m">1m</option>
                  <option value="5m">5m</option>
                  <option value="15m">15m</option>
                  <option value="30m">30m</option>
                  <option value="1h">1h</option>
                  <option value="4h">4h</option>
                  <option value="d">d</option>
                  <option value="w">w</option>
                  <option value="m">m</option>
                </select>
              </div>
            </div>
            </AddTradeSection>
          </div>

          {/* Row 2: Exit | Details | Share — vertical sections like Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0 items-stretch">
            <AddTradeSection
              title={t('addTrade.sectionExit')}
              highlight={prominentSections.includes(3) ? 'current' : 'upcoming'}
            >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.exitDate')}</label>
                <LocalizedDateTimeInput
                  value={exitDate}
                  onChange={setExitDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.exitPrice')}</label>
                <input
                  type="number"
                  step="any"
                  value={exitPrice}
                  onChange={(e) => setExitPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder={t('addTrade.exitPricePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.exitFee')}</label>
                <input
                  type="text"
                  value={exitFeeInput}
                  onChange={(e) => setExitFeeInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder={t('addTrade.feePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exit Setup Image URL</label>
                <input
                  type="url"
                  value={exitSetupImage}
                  onChange={(e) => setExitSetupImage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  placeholder="https://..."
                />
              </div>
            </div>
            </AddTradeSection>

            {/* Details */}
            <AddTradeSection title={t('addTrade.sectionDetails')}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position Size</label>
                <input
                  type="text"
                  value={positionSize !== null ? positionSize.toFixed(2) : '-'}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">P&L</label>
                <input
                  type="text"
                  value={pnl !== null ? pnl.toFixed(2) : '-'}
                  readOnly
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed text-sm ${
                    pnl !== null && pnl >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">P&L %</label>
                <input
                  type="text"
                  value={pnlPercentage !== null ? `${pnlPercentage.toFixed(2)}%` : '-'}
                  readOnly
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed text-sm ${
                    pnlPercentage !== null && pnlPercentage >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                />
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                placeholder={t('addTrade.notesPlaceholder')}
              />
            </div>
            {canUseAccounts && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.account')}</label>
                <select
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value="">{t('addTrade.accountPlaceholder')}</option>
                  {accountsLoading ? (
                    <option disabled>Loading accounts...</option>
                  ) : (
                    accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.platform} ({acc.currency})
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('addTrade.tags')}</label>
              <div className="flex flex-wrap gap-2 items-center px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent bg-white min-h-[42px]">
                {tagsArray.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 rounded-md text-sm font-bold"
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => setTagsArray((prev) => prev.filter((t) => t !== tag))}
                      className="hover:bg-orange-200 rounded p-0.5 -mr-0.5"
                      aria-label={`Remove ${tag}`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInputValue}
                  onChange={(e) => setTagInputValue(e.target.value)}
                  disabled={tagsArray.length >= 5}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault()
                      const word = tagInputValue.trim()
                      if (word && !tagsArray.includes(word)) {
                        if (tagsArray.length >= 5) {
                          showWarning(t('addTrade.tagsLimitReached'))
                        } else {
                          setTagsArray((prev) => [...prev, word])
                          setTagInputValue('')
                        }
                      } else if (e.key === ' ') {
                        setTagInputValue('')
                      }
                    } else if (e.key === 'Backspace' && !tagInputValue && tagsArray.length > 0) {
                      setTagsArray((prev) => prev.slice(0, -1))
                    }
                  }}
                  onBlur={() => {
                    const word = tagInputValue.trim()
                    if (word && !tagsArray.includes(word)) {
                      if (tagsArray.length >= 5) {
                        showWarning(t('addTrade.tagsLimitReached'))
                      } else {
                        setTagsArray((prev) => [...prev, word])
                        setTagInputValue('')
                      }
                    }
                  }}
                  className={`flex-1 min-w-[120px] outline-none text-sm py-0.5 ${tagsArray.length >= 5 ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  placeholder={tagsArray.length === 0 ? t('addTrade.tagsPlaceholder') : tagsArray.length >= 5 ? t('addTrade.tagsLimitReached') : ''}
                />
              </div>
            </div>
          </div>
            </AddTradeSection>

            {/* Action — Share + Cancel & Save */}
            <AddTradeSection title={t('addTrade.action')} highlight="normal">
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800">
                    Public share
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Makes this trade shareable. {t('tradeList.publicShareDescription')}
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 select-none">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    disabled={profileLoading || !canPublishTrades}
                    className="h-4 w-4 accent-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className={`text-sm ${profileLoading || !canPublishTrades ? 'text-gray-400' : 'text-gray-700'}`}>
                    {isPublic ? 'On' : 'Make public'}
                  </span>
                </label>
              </div>

              {isPublic && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Visibility
                  </label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="publicVisibility"
                        value="link"
                        checked={publicVisibility === 'link'}
                        onChange={() => setPublicVisibility('link')}
                        className="h-4 w-4 accent-orange-500"
                      />
                      <span className="text-sm text-gray-700">Link</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="publicVisibility"
                        value="community"
                        checked={publicVisibility === 'community'}
                        onChange={() => setPublicVisibility('community')}
                        className="h-4 w-4 accent-orange-500"
                      />
                      <span className="text-sm text-gray-700">Community</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="publicVisibility"
                        value="restricted"
                        checked={publicVisibility === 'restricted'}
                        onChange={() => setPublicVisibility('restricted')}
                        className="h-4 w-4 accent-orange-500"
                      />
                      <span className="text-sm text-gray-700">Followers</span>
                    </label>
                  </div>
                </div>
              )}

              <div className={`flex gap-3 pt-2 ${embedded ? 'sticky bottom-0' : ''}`}>
                <button
                  type="button"
                  onClick={() => {
                    resetForm()
                    onClose()
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  {t('addTrade.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {loading ? (isEdit ? t('addTrade.updating') : t('addTrade.saving')) : (isEdit ? t('addTrade.updateTrade') : t('addTrade.saveTrade'))}
                </button>
              </div>
            </div>
            </AddTradeSection>
          </div>
        </form>
    </div>
  )

  if (embedded) {
    return (
      <div className="w-full px-4 sm:px-6 py-6 pb-28 overflow-x-hidden min-w-0">
        {formCard}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="mx-4 my-8 max-h-[90vh] overflow-y-auto">
        {formCard}
      </div>
    </div>
  )
}

